import { asArray, isRecord, str } from './json'
import type { LatLon } from './types'

/**
 * Was mit der Antwort der Adresssuche zu geschehen hat, bevor daraus eine
 * Koordinate im Bestand wird.
 *
 * Reine Funktionen und kein React, kein fetch: der Netzaufruf steht in
 * data/api.ts, die Bedienung in features/stages - hier stehen nur die Regeln,
 * und die sind ohne Browser prüfbar. Dieselbe Aufteilung wie bei lib/route.ts.
 *
 * Warum hier noch einmal geprüft wird, obwohl der Server dieselbe Prüfung schon
 * gemacht hat: weil diese Seite es ist, die schreibt. Was aus dem Netz kommt,
 * ist von hier aus eine fremde Behauptung - auch dann, wenn es den eigenen
 * Server passiert hat. Eine unbrauchbare Koordinate, die durchrutscht, steht
 * danach im Reisedokument, geht durch den Abgleich auf alle Geräte und landet
 * im Navigationsgerät. Es ist derselbe Grundsatz wie beim Lesen einer
 * GPX-Datei (lib/gpx.ts).
 */

/** Ein Treffer der Adresssuche. */
export interface Place {
  /**
   * Die vollständige Bezeichnung aus OSM, etwa „Camping Bled, Kidričeva cesta,
   * Bled, 4263, Slovenija“. Der lange Name mit Absicht: an ihm entscheidet der
   * Nutzer, ob das der gemeinte Ort ist. „Camping Bled“ allein gibt es
   * mehrfach.
   */
  name: string
  /** Wo er liegt, als [Breite, Länge] - wie überall im Datenmodell. */
  at: LatLon
  /** Art des Ortes laut OSM, etwa „camp_site“. Leer heisst „unbekannt“. */
  kind: string
}

/**
 * Ist das eine Koordinate, die es auf der Erde gibt?
 *
 * Genau 0/0 fällt bewusst mit heraus - dieselbe Regel wie beim GPX-Leser
 * (usableCoord in lib/gpx.ts). Der Punkt liegt im Golf von Guinea, und dort
 * steht kein Campingplatz; so sieht ein leeres Feld aus, das jemand als Zahl
 * ausgegeben hat.
 */
export function usablePlaceCoord(lat: number, lon: number): boolean {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false
  return lat !== 0 || lon !== 0
}

/**
 * Macht aus der Antwort der Suche die Liste, die der Oberfläche vorgelegt wird.
 *
 * Ein unbrauchbarer Treffer fällt für sich heraus und verwirft nicht die ganze
 * Liste: eine Trefferliste ist eine Aufzählung, und eine Zeile weniger
 * behauptet nichts. Anders als bei der Geometrie einer Strecke, wo ein
 * übersprungener Punkt eine Abkürzung erfände, die es auf der Strasse nicht
 * gibt.
 *
 * @param raw das Feld results der Antwort, ungeprüft.
 */
export function placesFrom(raw: unknown): Place[] {
  const orte: Place[] = []
  for (const eintrag of asArray(raw)) {
    if (!isRecord(eintrag)) continue
    const name = str(eintrag.name).trim()
    if (name === '') continue
    const lat = zahl(eintrag.lat)
    const lon = zahl(eintrag.lon)
    if (!usablePlaceCoord(lat, lon)) continue
    orte.push({ name, at: [lat, lon], kind: str(eintrag.kind).trim() })
  }
  return orte
}

/** Der Ort zu einer Koordinate: was die Rückwärtssuche geliefert hat. */
export interface Ortstreffer {
  /** Die vollständige Bezeichnung aus OSM, wie bei Place. */
  name: string
  /** Der Punkt, den der Dienst benannt hat - nicht der erfragte. */
  at: LatLon
  /** Art des Ortes laut OSM, etwa „village“. Leer heisst „unbekannt“. */
  kind: string
  /** Länderkürzel nach ISO 3166-1 alpha-2, gross. Leer heisst „unbekannt“. */
  country: string
}

/**
 * Macht aus der Antwort der Rückwärtssuche einen Ort.
 *
 * Geprüft wird wie bei placesFrom, und aus demselben Grund: was aus dem Netz
 * kommt, ist von hier aus eine fremde Behauptung, auch wenn es den eigenen
 * Server passiert hat.
 *
 * @returns null, wenn dort kein Ort steht. Das ist der Normalfall auf offener
 *   See und keine Störung - der Aufrufer behält dann seine Koordinate als
 *   Beschriftung, und niemand hat einen Ort behauptet.
 */
export function ortAus(raw: unknown): Ortstreffer | null {
  if (!isRecord(raw)) return null
  const name = str(raw.name).trim()
  if (name === '') return null
  const lat = zahl(raw.lat)
  const lon = zahl(raw.lon)
  if (!usablePlaceCoord(lat, lon)) return null
  return {
    name,
    at: [lat, lon],
    kind: str(raw.kind).trim(),
    country: str(raw.country).trim().toUpperCase(),
  }
}

/**
 * Der Anfang einer Bezeichnung: was als Beschriftung einer Etappe taugt.
 *
 * Der lange Name entscheidet den Treffer, aber „Zofingen, Bezirk Zofingen,
 * Aargau, 4800, Schweiz“ steht sonst im Kopf der Etappenkarte und im gedruckten
 * Buch. Das erste Stück ist der Ort selbst - der Dienst sortiert seine
 * Bezeichnung vom Kleinen zum Grossen.
 */
export function kurzerOrt(name: string): string {
  return name.split(',')[0]?.trim() ?? ''
}

/**
 * Eine Zahl, die auch wirklich als Zahl geliefert wurde - sonst NaN.
 *
 * Ausdrücklich nicht num() aus lib/json.ts: dessen Umweg über Number() macht
 * aus einer leeren Zeichenkette eine 0, und eine 0 ist hier keine fehlende
 * Angabe, sondern ein Ort - der Äquator. Ein Treffer mit lat: "" läge damit im
 * Golf von Guinea, und zwar mit einer Länge, die plausibel aussieht.
 */
function zahl(v: unknown): number {
  return typeof v === 'number' ? v : NaN
}

/**
 * Die Koordinate eines Treffers als Text, wie sie unter der Trefferzeile steht.
 *
 * Fünf Nachkommastellen sind etwa ein Meter - genauer, als eine Einfahrt breit
 * ist, und dieselbe Genauigkeit, mit der Server und Zwischenspeicher rechnen.
 */
export function coordText([lat, lon]: LatLon): string {
  const kurz = (v: number) => String(Number(v.toFixed(5)))
  return `${kurz(lat)}, ${kurz(lon)}`
}
